import { useSuspenseQuery } from '@tanstack/react-query';
import { OnboardingTasksResponse, fetchOnboardingTasks } from 'api/onboarding';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { accountQueryKeys } from 'queries/account-query-keys';
import { Campaign } from 'types/domain';

interface TransformedOnboardingTasks {
    raised: number;
    firstCampaign: Campaign | null;
    percentageOutOfMax: number;
    hasRaisedFirstDollar: boolean;
    hasRaisedOverMax: boolean;
    hasCampaign: boolean;
    hasTransaction: boolean;
    hasBank: boolean;
    canUseReward?: boolean;
    didAckCta: boolean;
}

const RAISED_MAX = 1000;

const useOnboardingTasks = () => {
    const { accountId } = useCurrentAccount();
    const { data: tasks } = useSuspenseQuery({
        queryKey: accountQueryKeys.onboarding(accountId),
        queryFn: () => fetchOnboardingTasks({ accountId }),
        select: transformOnboardingTasks,
    });

    return {
        tasks,
    };
};

const transformOnboardingTasks = (data: OnboardingTasksResponse): TransformedOnboardingTasks => ({
    raised: data.raised,
    percentageOutOfMax: data.raised < RAISED_MAX ? (data.raised / RAISED_MAX) * 100 : 100,
    hasRaisedFirstDollar: data.raised > 0,
    hasRaisedOverMax: data.raised >= RAISED_MAX,
    hasTransaction: data.has_transaction,
    hasBank: data.has_payout_account,
    hasCampaign: data.has_campaign,
    firstCampaign: data.first_campaign,
    canUseReward: data.can_use_reward,
    didAckCta: !!data.reward_status,
});

export { useOnboardingTasks };
